import { Check, Search } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'

import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { optionId, type ListNav, type ListOption } from './listbox-nav'

/**
 * Lo que comparten `Select` y `MultiSelect`: la lista de opciones del panel.
 *
 * El foco **no entra a la lista**: queda en el disparador (o en el buscador,
 * si hay), y la opción activa se anuncia con `aria-activedescendant`. Es el
 * patrón de combobox de ARIA, y es lo que deja que las flechas, Enter y la
 * escritura funcionen sin que el foco salte entre elementos.
 */

export interface OptionListProps {
  id: string
  label: string
  nav: ListNav
  isSelected: (value: string) => boolean
  onPick: (option: ListOption) => void
  multiple?: boolean
  /** Muestra el buscador. Lo decide quien llama según la cantidad de opciones. */
  searchable: boolean
  /** Teclas del buscador: las mismas que el disparador. */
  onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  footer?: ReactNode
}

export function OptionList({
  id,
  label,
  nav,
  isSelected,
  onPick,
  multiple = false,
  searchable,
  onSearchKeyDown,
  footer,
}: OptionListProps) {
  const t = useT()
  const lista = useRef<HTMLUListElement>(null)

  // La activa siempre a la vista, también cuando se llega con el teclado.
  //
  // A mano y no con `scrollIntoView`: ése scrollea TODOS los ancestros, la
  // página incluida, y el panel sigue a su disparador — mover la página mueve
  // el disparador, y el panel se va con él fuera de la pantalla. Lo único que
  // tiene que scrollear es el panel.
  useEffect(() => {
    const nodo = lista.current?.querySelector<HTMLElement>(
      `#${CSS.escape(optionId(id, nav.active))}`,
    )
    const panel = nodo?.offsetParent as HTMLElement | null | undefined
    if (!nodo || !panel) return
    const arriba = nodo.offsetTop
    const abajo = arriba + nodo.offsetHeight
    if (arriba < panel.scrollTop) panel.scrollTop = arriba
    else if (abajo > panel.scrollTop + panel.clientHeight) {
      panel.scrollTop = abajo - panel.clientHeight
    }
  }, [id, nav.active])

  return (
    <div className="flex flex-col">
      {searchable ? (
        <label className="hairline-b relative flex items-center">
          <Search
            size={12}
            aria-hidden="true"
            className="pointer-events-none absolute left-2 text-text-3"
          />
          <input
            autoFocus
            type="text"
            role="searchbox"
            aria-label={t('common.select.search')}
            aria-controls={id}
            aria-activedescendant={nav.visibles.length ? optionId(id, nav.active) : undefined}
            placeholder={t('common.select.search')}
            value={nav.query}
            onChange={(event) => nav.setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            className="h-8 w-full bg-transparent pr-2 pl-7 text-xs text-text-1 outline-none placeholder:text-text-3"
          />
        </label>
      ) : null}

      <ul
        ref={lista}
        id={id}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multiple || undefined}
        className="flex flex-col p-1"
      >
        {nav.visibles.length === 0 ? (
          <li className="px-2 py-1.5 text-2xs text-text-3">{t('common.select.noResults')}</li>
        ) : (
          nav.visibles.map((opcion, index) => {
            const elegida = isSelected(opcion.value)
            return (
              <li
                key={opcion.value}
                id={optionId(id, index)}
                role="option"
                data-value={opcion.value}
                aria-selected={elegida}
                aria-disabled={opcion.disabled || undefined}
                // `mousedown` y no `click`: el blur del disparador no puede
                // ganarle a la elección.
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => nav.setActive(index)}
                onClick={() => {
                  if (!opcion.disabled) onPick(opcion)
                }}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-[var(--radius-control)] px-2 py-1 text-xs text-text-1',
                  index === nav.active && 'bg-surface-2',
                  opcion.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {multiple ? (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border',
                      elegida ? 'border-signal bg-signal text-on-signal' : 'border-hairline',
                    )}
                  >
                    {elegida ? <Check size={10} strokeWidth={3} /> : null}
                  </span>
                ) : null}
                {opcion.leading}
                <span className="min-w-0 flex-1 truncate">{opcion.label}</span>
                {!multiple && elegida ? (
                  <Check size={12} aria-hidden="true" className="shrink-0 text-text-2" />
                ) : null}
              </li>
            )
          })
        )}
      </ul>

      {footer}
    </div>
  )
}
